export interface Resource {
  id: number | string;
  slug: string;
  title: string;
  type: 'Whitepaper' | 'Case Study' | 'Guide' | 'Checklist' | 'Webinar' | 'Infographic';
  topic: string;
  description: string;
  image: string;
  date: string;
  readTime: string;
  featured: boolean;
  downloadable: boolean;
  tags: string[];
  excerpt: string;
  metrics?: { value: string; label: string }[];
  status?: 'published' | 'draft';
  /** Gated guide identifier — see src/lib/resourceGuides.ts. */
  guideId?: string;
  /** Public site-assets URL of the branded downloadable PDF guide. */
  guidePdfUrl?: string;
}

export const resources: Resource[] = [
  {
    id: 1,
    slug: 'erp-modernization-whitepaper-2025',
    title: 'ERP Modernization in 2025: The Complete Enterprise Playbook',
    type: 'Whitepaper',
    topic: 'ERP & Enterprise',
    description: 'A deep-dive into ERP migration strategies, platform comparisons (SAP, Oracle, Microsoft Dynamics), and ROI frameworks for enterprise organizations planning their next major transformation.',
    excerpt: 'Enterprise resource planning is at an inflection point. With cloud-native ERP platforms maturing rapidly and legacy systems creating mounting technical debt, 2025 is proving to be the breakout year for ERP modernization investments across mid-market and enterprise organizations.',
    image: 'https://readdy.ai/api/search-image?query=enterprise%20ERP%20modernization%20whitepaper%20professional%20business%20technology%20visualization%20with%20abstract%20data%20flows%20teal%20and%20charcoal%20color%20scheme%20clean%20minimal%20corporate%20design%202025&width=800&height=500&seq=res-1&orientation=landscape',
    date: 'March 2025',
    readTime: '22 min read',
    featured: true,
    downloadable: true,
    tags: ['ERP', 'SAP', 'Oracle', 'Microsoft Dynamics', 'Migration', 'Cloud ERP'],
  },
  {
    id: 2,
    slug: 'healthcare-it-transformation-case-study',
    title: 'How a 12-Hospital System Cut IT Costs by 38% with Managed Services',
    type: 'Case Study',
    topic: 'Healthcare IT',
    description: 'Discover how Cendien helped a major regional health system consolidate IT operations, eliminate redundant systems, reduce downtime, and achieve HIPAA compliance across all facilities — saving $4.2M annually.',
    excerpt: 'When a 12-hospital regional health system came to Cendien with fragmented IT infrastructure across all facilities, an outdated service desk model, and looming compliance deadlines, the team needed a partner who understood both healthcare operations and enterprise IT at scale.',
    image: 'https://readdy.ai/api/search-image?query=healthcare%20hospital%20modern%20IT%20transformation%20case%20study%20professional%20clinical%20environment%20clean%20white%20and%20teal%20medical%20technology%20success%20story&width=800&height=500&seq=res-2&orientation=landscape',
    date: 'February 2025',
    readTime: '12 min read',
    featured: true,
    downloadable: true,
    tags: ['Healthcare', 'Managed Services', 'HIPAA', 'Cost Reduction', 'EHR'],
    metrics: [
      { value: '38%', label: 'IT cost reduction' },
      { value: '$4.2M', label: 'Annual savings' },
      { value: '12', label: 'Hospitals consolidated' },
    ],
  },
  {
    id: 3,
    slug: 'zero-trust-implementation-checklist',
    title: 'Zero Trust Implementation Checklist',
    type: 'Checklist',
    topic: 'Cybersecurity',
    description: 'Zero trust is not a product. NIST SP 800-207 (August 2020) describes it as a set of principles in which no user, device or network location is trusted by default, and every access request is evaluated based on identity, device state and context. CISA\'s Zero Trust Maturity Model, Version 2.0 (April 2023), organizes the work into five pillars — Identity, Devices, Networks, Applications & Workloads, and Data — plus three cross-cutting capabilities: Visibility & Analytics, Automation & Orchestration, and Governance. Use this checklist to see where you are and pick the next realistic step.',
    guideId: 'zero-trust-implementation-checklist',
    guidePdfUrl: 'https://dowywtpgmxlypuokhxfx.supabase.co/storage/v1/object/public/site-assets/guides/zero-trust-implementation-checklist.pdf',
    image: 'https://readdy.ai/api/search-image?query=cybersecurity%20zero%20trust%20security%20checklist%20shield%20protection%20digital%20network%20visualization%20dark%20professional%20background%20teal%20accent%20lighting%20enterprise%20security&width=800&height=500&seq=res-3&orientation=landscape',
    date: 'January 2025',
    readTime: '8 min read',
    featured: false,
    downloadable: true,
    tags: ['Cybersecurity', 'Zero Trust', 'Compliance', 'Identity', 'Network Security'],
  },
  {
    id: 4,
    slug: 'cloud-migration-enterprise-playbook',
    title: 'The Enterprise Cloud Migration Playbook: AWS, Azure & GCP',
    type: 'Guide',
    topic: 'Cloud & Infrastructure',
    description: 'Step-by-step guidance for planning and executing cloud migrations — covering AWS, Azure, and GCP with workload assessment templates, risk frameworks, and real-world migration timelines.',
    excerpt: 'Cloud migration projects fail not because the technology isn\'t ready — but because organizations underestimate the planning, governance, and change management required. This playbook gives you the full picture.',
    image: 'https://readdy.ai/api/search-image?query=enterprise%20cloud%20migration%20strategy%20planning%20abstract%20digital%20technology%20visualization%20professional%20teal%20and%20slate%20color%20palette%20clean%20modern%20corporate%20design%20no%20text%20no%20words%20pure%20visual&width=800&height=500&seq=res-4-v2&orientation=landscape',
    date: 'March 2025',
    readTime: '18 min read',
    featured: true,
    downloadable: true,
    tags: ['Cloud', 'AWS', 'Azure', 'GCP', 'Migration', 'Infrastructure'],
  },
  {
    id: 5,
    slug: 'cmmc-2-defense-contractors-webinar',
    title: 'Webinar: Navigating CMMC 2.0 for Defense Contractors',
    type: 'Webinar',
    topic: 'Government & Compliance',
    description: 'On-demand recording of our 60-minute expert panel on CMMC 2.0 requirements, assessment preparation strategies, and the most common compliance pitfalls that disqualify defense contractors.',
    excerpt: 'CMMC 2.0 enforcement is no longer hypothetical. In this on-demand session, our government compliance specialists break down what defense contractors need to know — and do — before their next DoD contract bid.',
    image: 'https://readdy.ai/api/search-image?query=government%20federal%20compliance%20cybersecurity%20CMMC%20defense%20contractor%20webinar%20recording%20professional%20conference%20setting%20teal%20and%20slate%20modern%20corporate&width=800&height=500&seq=res-5&orientation=landscape',
    date: 'February 2025',
    readTime: '60 min watch',
    featured: false,
    downloadable: false,
    tags: ['Government', 'CMMC', 'Compliance', 'DoD', 'Cybersecurity'],
  },
  {
    id: 6,
    slug: 'it-staffing-cost-calculator-guide',
    title: 'IT Staffing Cost Calculator & Hiring Model Comparison Guide',
    type: 'Guide',
    topic: 'IT Staffing',
    description: 'Compare the true total cost of contract, contract-to-hire, and direct placement staffing models with our interactive calculation framework, real-world benchmarks, and decision criteria.',
    excerpt: 'The sticker price of a contractor rate rarely reflects the true cost of IT talent acquisition. This guide walks through a complete cost model — from time-to-fill and onboarding overhead to long-term strategic fit — so you can make the right staffing decision.',
    image: 'https://readdy.ai/api/search-image?query=IT%20staffing%20talent%20hiring%20cost%20analysis%20guide%20professional%20team%20collaboration%20modern%20corporate%20office%20warm%20orange%20and%20slate%20professional%20business%20setting&width=800&height=500&seq=res-6&orientation=landscape',
    date: 'January 2025',
    readTime: '10 min read',
    featured: false,
    downloadable: true,
    tags: ['IT Staffing', 'Hiring', 'Cost Analysis', 'Talent', 'Workforce'],
  },
  {
    id: 7,
    slug: 'ai-enterprise-readiness-whitepaper',
    title: 'AI Readiness Assessment: Is Your Enterprise Infrastructure GenAI-Ready?',
    type: 'Whitepaper',
    topic: 'Analytics & AI',
    description: 'A comprehensive maturity framework for evaluating your organization\'s data infrastructure, governance practices, and workforce readiness before deploying generative AI at scale.',
    excerpt: 'The pressure to adopt generative AI is intense — but the organizations seeing real ROI are the ones who assessed their readiness before jumping in. This whitepaper gives you a structured framework to do exactly that.',
    image: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20enterprise%20readiness%20assessment%20futuristic%20AI%20visualization%20business%20analytics%20data%20strategy%20corporate%20setting%20orange%20and%20teal%20gradient%20professional&width=800&height=500&seq=res-7&orientation=landscape',
    date: 'March 2025',
    readTime: '20 min read',
    featured: false,
    downloadable: true,
    tags: ['AI', 'GenAI', 'Analytics', 'Data Strategy', 'Machine Learning'],
  },
  {
    id: 8,
    slug: 'state-agency-fedramp-case-study',
    title: 'State Agency Achieves FedRAMP Authorization in 9 Months — On Budget',
    type: 'Case Study',
    topic: 'Government & Compliance',
    description: 'How Cendien guided a state technology agency through the full FedRAMP authorization process — delivering a fully compliant cloud environment three months ahead of schedule and 12% under budget.',
    excerpt: 'FedRAMP authorization is notoriously complex — most agencies take 18-24 months and overshoot their budgets. Learn how a state CIO office partnered with Cendien to compress the timeline without compromising security rigor.',
    image: 'https://readdy.ai/api/search-image?query=government%20state%20agency%20FedRAMP%20cloud%20authorization%20federal%20building%20digital%20compliance%20visualization%20professional%20teal%20and%20slate%20color%20scheme%20security&width=800&height=500&seq=res-8&orientation=landscape',
    date: 'December 2024',
    readTime: '10 min read',
    featured: false,
    downloadable: true,
    tags: ['Government', 'FedRAMP', 'Cloud', 'Compliance', 'State Government'],
    metrics: [
      { value: '9 mo', label: 'Authorization timeline' },
      { value: '12%', label: 'Under budget' },
      { value: '3 mo', label: 'Ahead of schedule' },
    ],
  },
  {
    id: 9,
    slug: 'hipaa-security-rule-checklist-2025',
    title: 'HIPAA Security Rule Compliance Checklist 2025 — Updated for Proposed Rule Changes',
    type: 'Checklist',
    topic: 'Healthcare IT',
    description: 'A 60-point checklist aligned with the proposed 2025 HIPAA Security Rule updates — covering mandatory MFA, encryption standards, breach notification timelines, and updated risk analysis requirements.',
    excerpt: 'The proposed 2025 HIPAA Security Rule updates represent the most significant regulatory changes in over a decade. This checklist maps every new requirement to concrete implementation steps your IT and compliance teams can act on today.',
    image: 'https://readdy.ai/api/search-image?query=HIPAA%20healthcare%20compliance%20checklist%20medical%20data%20security%20regulatory%20documentation%20clinical%20office%20environment%20clean%20white%20teal%20professional%20healthcare%20IT%202025&width=800&height=500&seq=res-9&orientation=landscape',
    date: 'February 2025',
    readTime: '6 min read',
    featured: false,
    downloadable: true,
    tags: ['Healthcare', 'HIPAA', 'Compliance', 'Security', 'PHI'],
  },
  {
    id: 10,
    slug: 'managed-services-roi-webinar',
    title: 'Webinar: Calculating the True ROI of Managed IT Services',
    type: 'Webinar',
    topic: 'Managed Services',
    description: 'Our practice directors walk through a live ROI model showing how to quantify cost savings, risk reduction, and strategic value from an MSP engagement — with real client benchmarks.',
    excerpt: 'Too many organizations underestimate or misframe the ROI of managed IT services. In this session, our practice directors break down a multi-dimension value model that goes beyond simple cost comparison.',
    image: 'https://readdy.ai/api/search-image?query=managed%20IT%20services%20ROI%20business%20financial%20analysis%20charts%20professional%20consulting%20team%20modern%20office%20warm%20lighting%20technology%20displays%20enterprise&width=800&height=500&seq=res-10&orientation=landscape',
    date: 'January 2025',
    readTime: '45 min watch',
    featured: false,
    downloadable: false,
    tags: ['Managed Services', 'ROI', 'Cost Savings', 'MSP', 'IT Strategy'],
  },
  {
    id: 11,
    slug: 'digital-transformation-roadmap-template',
    title: 'Digital Transformation Roadmap Template for Mid-Market Enterprises',
    type: 'Guide',
    topic: 'Strategy',
    description: 'A ready-to-use roadmap template with phased milestones, stakeholder alignment tools, KPI frameworks, and governance structures for organizations beginning their digital transformation journey.',
    excerpt: 'Digital transformation fails when it\'s treated as a technology project rather than a business strategy initiative. This template gives mid-market organizations the planning framework to get it right — from discovery through execution.',
    image: 'https://readdy.ai/api/search-image?query=digital%20transformation%20roadmap%20template%20strategic%20planning%20visualization%20business%20growth%20milestones%20professional%20corporate%20setting%20orange%20and%20teal%20accents%20modern%20design&width=800&height=500&seq=res-11&orientation=landscape',
    date: 'November 2024',
    readTime: '14 min read',
    featured: false,
    downloadable: true,
    tags: ['Strategy', 'Digital Transformation', 'Roadmap', 'Change Management'],
  },
  {
    id: 12,
    slug: 'cybersecurity-threat-landscape-2025',
    title: '2025 Cybersecurity Threat Landscape: Enterprise Risk Infographic',
    type: 'Infographic',
    topic: 'Cybersecurity',
    description: 'A visual breakdown of the top 10 cyber threats facing enterprises in 2025 — including AI-powered attack vectors, ransomware group activity, and sector-specific risk exposure data.',
    excerpt: 'The 2025 threat landscape is defined by three forces: AI-powered adversaries, expanding attack surfaces, and increasingly targeted supply chain attacks. Our infographic makes the data actionable for security teams and executives alike.',
    image: 'https://readdy.ai/api/search-image?query=cybersecurity%20threat%20landscape%202025%20infographic%20visual%20data%20charts%20attack%20vector%20statistics%20dark%20professional%20design%20teal%20and%20orange%20accent%20colors%20enterprise%20security&width=800&height=500&seq=res-12&orientation=landscape',
    date: 'March 2025',
    readTime: '3 min read',
    featured: false,
    downloadable: true,
    tags: ['Cybersecurity', 'Threat Intelligence', 'Ransomware', 'AI Security', '2025'],
  },
  {
    id: 13,
    slug: 'sap-s4hana-migration-whitepaper',
    title: 'SAP S/4HANA Migration: Lessons from 40+ Enterprise Deployments',
    type: 'Whitepaper',
    topic: 'ERP & Enterprise',
    description: 'Cendien\'s SAP practice shares aggregated insights from over 40 S/4HANA implementations — covering Greenfield vs. Brownfield decisions, integration complexity, and the most critical success factors.',
    excerpt: 'No two SAP S/4HANA migrations are identical — but patterns emerge when you\'ve led 40+ of them. This whitepaper distills those lessons into a practical guide that helps project teams avoid the most common and costly mistakes.',
    image: 'https://readdy.ai/api/search-image?query=SAP%20S4HANA%20enterprise%20migration%20whitepaper%20professional%20technology%20abstract%20business%20data%20visualization%20teal%20charcoal%20clean%20corporate%20design%20ERP%20platform&width=800&height=500&seq=res-13&orientation=landscape',
    date: 'February 2025',
    readTime: '25 min read',
    featured: false,
    downloadable: true,
    tags: ['SAP', 'S/4HANA', 'ERP', 'Migration', 'Implementation'],
  },
  {
    id: 14,
    slug: 'manufacturing-digital-transformation-case-study',
    title: 'Mid-Size Manufacturer Achieves 99.4% Uptime After ERP & MES Integration',
    type: 'Case Study',
    topic: 'ERP & Enterprise',
    description: 'How a $340M manufacturer partnered with Cendien to integrate their ERP with manufacturing execution systems, eliminate production downtime, and reduce IT overhead by 29%.',
    excerpt: 'When production floors run on disconnected systems, the cost shows up in downtime, quality escapes, and missed delivery windows. This case study shows how one manufacturer fixed all three by connecting their ERP to the shop floor.',
    image: 'https://readdy.ai/api/search-image?query=manufacturing%20digital%20transformation%20ERP%20integration%20production%20floor%20technology%20modern%20factory%20automation%20professional%20slate%20and%20teal%20color%20scheme%20industrial%20technology&width=800&height=500&seq=res-14&orientation=landscape',
    date: 'January 2025',
    readTime: '11 min read',
    featured: false,
    downloadable: true,
    tags: ['Manufacturing', 'ERP', 'MES', 'Integration', 'Uptime'],
    metrics: [
      { value: '99.4%', label: 'Uptime' },
      { value: '29%', label: 'IT overhead reduction' },
      { value: '$340M', label: 'Client revenue' },
    ],
  },
  {
    id: 15,
    slug: 'genai-implementation-guide-enterprise',
    title: 'From Pilot to Production: Enterprise GenAI Implementation Guide',
    type: 'Guide',
    topic: 'Analytics & AI',
    description: 'A practical guide for scaling AI proof-of-concepts into production deployments — covering data governance, model selection, change management, and risk frameworks for enterprise AI teams.',
    excerpt: 'Most organizations have run at least one GenAI pilot by now. The hard part isn\'t starting — it\'s scaling. This guide addresses the governance, infrastructure, and organizational change challenges that separate pilots from production systems.',
    image: 'https://readdy.ai/api/search-image?query=generative%20AI%20implementation%20guide%20enterprise%20production%20deployment%20data%20governance%20technology%20visualization%20futuristic%20orange%20and%20teal%20gradient%20professional%20modern%20design&width=800&height=500&seq=res-15&orientation=landscape',
    date: 'March 2025',
    readTime: '16 min read',
    featured: false,
    downloadable: true,
    tags: ['AI', 'GenAI', 'Machine Learning', 'Data Governance', 'Implementation'],
  },
  {
    id: 16,
    slug: 'retail-omnichannel-it-case-study',
    title: 'National Retailer Unifies 200+ Stores with Single-Platform IT Architecture',
    type: 'Case Study',
    topic: 'Strategy',
    description: 'How Cendien helped a national specialty retailer consolidate disparate store systems, standardize infrastructure across 200+ locations, and reduce IT support costs by 44%.',
    excerpt: 'Retail IT fragmentation is a silent margin killer. When every store runs different systems with different support models, costs compound. This case study shows how one retailer broke the cycle with a unified platform approach.',
    image: 'https://readdy.ai/api/search-image?query=retail%20omnichannel%20technology%20IT%20architecture%20unification%20national%20chain%20store%20systems%20modern%20clean%20professional%20teal%20and%20white%20corporate%20business%20case%20study&width=800&height=500&seq=res-16&orientation=landscape',
    date: 'December 2024',
    readTime: '9 min read',
    featured: false,
    downloadable: true,
    tags: ['Retail', 'IT Strategy', 'Infrastructure', 'Managed Services', 'Cost Reduction'],
    metrics: [
      { value: '200+', label: 'Stores unified' },
      { value: '44%', label: 'Support cost reduction' },
      { value: '1', label: 'Unified platform' },
    ],
  },
  {
    id: 17,
    slug: 'incident-response-playbook-checklist',
    title: 'Incident Response Playbook & Checklist',
    type: 'Checklist',
    topic: 'Cybersecurity',
    description: 'NIST finalized SP 800-61 Revision 3 in April 2025. It supersedes Revision 2 and no longer treats incident response as a stand-alone lifecycle. Instead, it maps incident response activities to the six Functions of the NIST Cybersecurity Framework (CSF) 2.0: Govern, Identify, Protect, Detect, Respond and Recover. This playbook turns that guidance into a practical checklist you can adapt to your organization. It is a planning aid, not legal advice; confirm notification and reporting obligations with your counsel and any regulator, insurer or contract that applies to you.',
    guideId: 'incident-response-playbook-checklist',
    guidePdfUrl: 'https://dowywtpgmxlypuokhxfx.supabase.co/storage/v1/object/public/site-assets/guides/incident-response-playbook-checklist.pdf',
    image: 'https://readdy.ai/api/search-image?query=cybersecurity%20incident%20response%20playbook%20checklist%20NIST%20security%20operations%20center%20digital%20threat%20visualization%20dark%20professional%20teal%20accent%20enterprise%20security%20operations&width=800&height=500&seq=res-17&orientation=landscape',
    date: 'February 2025',
    readTime: '7 min read',
    featured: false,
    downloadable: true,
    tags: ['Cybersecurity', 'Incident Response', 'NIST', 'Breach', 'Security Operations'],
  },
  {
    id: 18,
    slug: 'cloud-cost-optimization-webinar',
    title: 'Webinar: Cutting Cloud Spend by 30%+ Without Sacrificing Performance',
    type: 'Webinar',
    topic: 'Cloud & Infrastructure',
    description: 'On-demand session where our cloud architects walk through real-world FinOps strategies — reserved instance planning, rightsizing, multi-cloud arbitrage, and governance models that eliminate waste.',
    excerpt: 'The average enterprise wastes 35% of their cloud budget on idle resources, over-provisioned instances, and untagged workloads. This on-demand session gives you a concrete plan to stop the bleeding.',
    image: 'https://readdy.ai/api/search-image?query=cloud%20cost%20optimization%20FinOps%20webinar%20financial%20performance%20cloud%20computing%20data%20analytics%20modern%20office%20professional%20orange%20accent%20teal%20slate%20corporate%20technology&width=800&height=500&seq=res-18&orientation=landscape',
    date: 'January 2025',
    readTime: '50 min watch',
    featured: false,
    downloadable: false,
    tags: ['Cloud', 'FinOps', 'Cost Optimization', 'AWS', 'Azure', 'Multi-Cloud'],
  },
  {
    id: 19,
    slug: 'nonprofit-it-governance-checklist',
    title: 'Nonprofit IT Governance & Security Compliance Checklist',
    type: 'Checklist',
    topic: 'Strategy',
    description: 'A 40-point governance checklist designed specifically for nonprofit IT leaders — covering donor data security, grant compliance requirements, remote workforce management, and budget accountability.',
    excerpt: 'Nonprofits face enterprise-grade security and compliance obligations with a fraction of the IT budget. This checklist helps executive directors, IT managers, and board members align on governance fundamentals without the enterprise price tag.',
    image: 'https://readdy.ai/api/search-image?query=nonprofit%20organization%20IT%20governance%20compliance%20checklist%20professional%20clean%20modern%20design%20teal%20and%20white%20nonprofit%20technology%20management%20corporate%20office&width=800&height=500&seq=res-19&orientation=landscape',
    date: 'November 2024',
    readTime: '5 min read',
    featured: false,
    downloadable: true,
    tags: ['Nonprofit', 'IT Governance', 'Compliance', 'Security', 'Budget Management'],
  },
  {
    id: 20,
    slug: 'financial-services-it-compliance-infographic',
    title: 'Financial Services IT Compliance: What Every CIO Needs to Know in 2025',
    type: 'Infographic',
    topic: 'Strategy',
    description: 'A visual map of the intersecting compliance obligations for financial services IT — SOX, PCI-DSS, GLBA, SEC cybersecurity rules, and DORA — with timelines and ownership guidance.',
    excerpt: 'The regulatory landscape for financial services IT has never been more complex. This visual guide maps overlapping requirements, key deadlines, and ownership responsibilities so your compliance strategy doesn\'t miss a beat.',
    image: 'https://readdy.ai/api/search-image?query=financial%20services%20IT%20compliance%20infographic%202025%20regulation%20framework%20banking%20fintech%20professional%20visualization%20orange%20and%20slate%20corporate%20data%20visual&width=800&height=500&seq=res-20&orientation=landscape',
    date: 'March 2025',
    readTime: '4 min read',
    featured: false,
    downloadable: true,
    tags: ['Financial Services', 'Compliance', 'SOX', 'PCI-DSS', 'DORA', 'SEC'],
  },
  {
    id: 21,
    slug: 'erp-training-best-practices-guide',
    title: 'ERP End-User Training That Actually Sticks: A Practitioner\'s Guide',
    type: 'Guide',
    topic: 'ERP & Enterprise',
    description: 'Practical guidance for designing ERP training programs that drive adoption, reduce post-go-live tickets, and deliver measurable productivity gains — drawn from 80+ implementation projects.',
    excerpt: 'Poor end-user training is the number one reason ERP implementations fail to deliver expected ROI. This guide gives training designers, project managers, and change leads a proven methodology for building programs that work.',
    image: 'https://readdy.ai/api/search-image?query=ERP%20end%20user%20training%20program%20corporate%20learning%20technology%20professional%20team%20workshop%20modern%20office%20teal%20and%20white%20clean%20business%20education%20enterprise%20software&width=800&height=500&seq=res-21&orientation=landscape',
    date: 'October 2024',
    readTime: '12 min read',
    featured: false,
    downloadable: true,
    tags: ['ERP', 'Training', 'Change Management', 'Adoption', 'End-User'],
  },
  {
    id: 22,
    slug: 'it-staffing-trends-infographic-2025',
    title: 'IT Staffing Trends & Salary Benchmarks: 2025 Mid-Year Report',
    type: 'Infographic',
    topic: 'IT Staffing',
    description: 'A visual benchmark report covering average IT contractor rates, in-demand skill premiums, hiring velocity trends, and the contract-vs-direct-hire balance across 12 specializations.',
    excerpt: 'The IT talent market in 2025 is bifurcated: AI and cloud roles command record premiums while legacy platform skills face headwinds. Our infographic cuts through the noise with current market data your hiring teams can actually use.',
    image: 'https://readdy.ai/api/search-image?query=IT%20staffing%20talent%20market%20trends%202025%20salary%20benchmark%20infographic%20professional%20data%20visualization%20orange%20and%20slate%20corporate%20technology%20workforce%20analytics&width=800&height=500&seq=res-22&orientation=landscape',
    date: 'March 2025',
    readTime: '5 min read',
    featured: false,
    downloadable: true,
    tags: ['IT Staffing', 'Salary Benchmarks', 'Talent Market', 'Hiring Trends', '2025'],
  },
  {
    id: 23,
    slug: 'healthcare-cloud-migration-case-study',
    title: 'Regional Health Network Migrates 3 Data Centers to Azure in 14 Months',
    type: 'Case Study',
    topic: 'Healthcare IT',
    description: 'How Cendien executed a complex multi-datacenter cloud migration for a regional health network — delivering HIPAA-compliant Azure infrastructure, 40% cost reduction, and zero patient care disruptions.',
    excerpt: 'Healthcare cloud migrations carry unique stakes: a misconfiguration isn\'t just a technical incident — it can disrupt patient care. This case study details how Cendien\'s healthcare cloud team executed a zero-disruption migration across three legacy data centers.',
    image: 'https://readdy.ai/api/search-image?query=healthcare%20cloud%20migration%20Azure%20hospital%20network%20data%20center%20professional%20clean%20modern%20clinical%20IT%20technology%20teal%20and%20white%20healthcare%20digital%20transformation%20case%20study&width=800&height=500&seq=res-23&orientation=landscape',
    date: 'January 2025',
    readTime: '13 min read',
    featured: false,
    downloadable: true,
    tags: ['Healthcare', 'Cloud Migration', 'Azure', 'HIPAA', 'Data Center'],
    metrics: [
      { value: '40%', label: 'Cost reduction' },
      { value: '3', label: 'Data centers migrated' },
      { value: '0', label: 'Care disruptions' },
    ],
  },
  {
    id: 24,
    slug: 'workday-support-managed-services-case-study',
    title: 'Workday Support & Managed Services: Stabilizing a Global HCM Platform',
    type: 'Case Study',
    topic: 'ERP & Enterprise',
    description: 'How Cendien assumed full Workday HCM support for a multinational organization with 40,000+ employees, eliminating recurring integration failures and accelerating resolution while freeing the internal team to focus on strategic initiatives.',
    excerpt: 'When a global enterprise found its Workday HCM environment consuming an outsized share of IT resources — with recurring integration failures, a growing support backlog, and internal admins stretched thin — the organization needed a partner who could take full ownership of day-to-day operations and stabilize the platform.',
    image: 'https://readdy.ai/api/search-image?query=modern%20enterprise%20human%20resources%20technology%20dashboard%20showing%20Workday%20HCM%20workforce%20analytics%20with%20colorful%20data%20charts%20and%20graphs%20on%20a%20large%20monitor%20in%20a%20bright%20clean%20professional%20office%20environment%20with%20teal%20and%20slate%20color%20tones%20and%20soft%20natural%20lighting&width=800&height=500&seq=res-24&orientation=landscape',
    date: 'August 2026',
    readTime: '9 min read',
    featured: false,
    downloadable: true,
    tags: ['Workday', 'Managed Services', 'HCM', 'Support', 'Cloud', 'ERP'],
    metrics: [
      { value: '99.9%', label: 'System uptime' },
      { value: '43%', label: 'Faster resolution' },
      { value: '$1.8M', label: 'Annual savings' },
      { value: '40K+', label: 'Employees supported' },
    ],
  },
  {
    id: 25,
    slug: 'infor-cloudsuite-manufacturing-case-study',
    title: 'Infor CloudSuite Implementation Unifies 6 Plants for a Global Manufacturer',
    type: 'Case Study',
    topic: 'ERP & Enterprise',
    description: 'How Cendien consolidated six disconnected manufacturing plants onto a single Infor CloudSuite Industrial instance — delivering real-time visibility, faster month-end close, and a 20% reduction in inventory carrying costs.',
    excerpt: 'A global manufacturer running six plants on fragmented legacy systems faced the classic ERP dilemma: each facility had its own processes, its own data silos, and its own version of the truth. This case study shows how a single Infor CloudSuite deployment broke down those walls.',
    image: 'https://readdy.ai/api/search-image?query=modern%20manufacturing%20facility%20with%20Infor%20CloudSuite%20ERP%20dashboard%20on%20large%20monitors%20showing%20production%20supply%20chain%20and%20financial%20analytics%20clean%20organized%20industrial%20environment%20with%20engineers%20reviewing%20data%20teal%20and%20slate%20color%20tones%20professional%20corporate%20technology&width=800&height=500&seq=res-25&orientation=landscape',
    date: 'July 2026',
    readTime: '11 min read',
    featured: false,
    downloadable: true,
    tags: ['Infor', 'CloudSuite', 'ERP', 'Manufacturing', 'Implementation', 'M3'],
    metrics: [
      { value: '6', label: 'Plants unified' },
      { value: '32%', label: 'Faster month-end close' },
      { value: '20%', label: 'Inventory cost reduction' },
      { value: '$2.7M', label: 'Annual savings' },
    ],
  },
  {
    id: 26,
    slug: 'ai-park-district-operations-case-study',
    title: 'AI-Powered Park Operations: 90% Bandwidth Reduction and Automated Guest Services',
    type: 'Case Study',
    topic: 'Analytics & AI',
    description: 'How Cendien delivered a governed, three-year AI roadmap for a major U.S. park district — including edge-based animal behavior monitoring, AI document intelligence for operational audits, and a WCAG-compliant multilingual guest virtual agent — reducing cloud bandwidth costs by up to 95% and enabling 24/7 wildlife welfare monitoring.',
    excerpt: 'A major park district managing 25,000+ acres across multiple reservations and a zoo needed to scale AI across conservation, guest services, and back-office operations without compromising governance, accessibility, or public trust. Cendien built a governed AI portfolio inside the district\'s own Microsoft tenant, delivering production-grade computer vision, retrieval-augmented document intelligence, and conversational agents — all aligned to NIST AI RMF and public-records requirements.',
    image: 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20vast%20public%20park%20district%20with%20lush%20green%20forests%20winding%20trails%20and%20a%20modern%20zoo%20facility%20under%20bright%20daylight%20with%20clean%20natural%20environment%20and%20soft%20warm%20lighting%20professional%20photography%20for%20technology%20case%20study&width=800&height=500&seq=res-26&orientation=landscape',
    date: 'August 2026',
    readTime: '14 min read',
    featured: false,
    downloadable: true,
    tags: ['AI', 'Public Sector', 'Computer Vision', 'Document Intelligence', 'Guest Experience', 'Edge Computing'],
    metrics: [
      { value: '90%', label: 'Bandwidth cost reduction' },
      { value: '50-70%', label: 'Faster document review' },
      { value: '30-50%', label: 'Guest inquiry self-service' },
      { value: '25K+', label: 'Acres managed' },
    ],
  },
  {
    id: 27,
    slug: 'municipal-it-managed-services-public-safety-case-study',
    title: '24x7 Municipal IT Managed Services: 30-Minute Public-Safety Response Across 7 City Sites',
    type: 'Case Study',
    topic: 'Managed Services',
    description: 'How Cendien assumed full IT managed services for a Texas municipal government — covering Police, Fire, Public Works, and Utility Billing across seven facilities — delivering CJIS-compliant cybersecurity, a guaranteed 30-minute on-site public-safety response, and 4-hour resolution targets under a fixed three-year budget.',
    excerpt: 'A Texas city with roughly 48 users across seven facilities needed a single accountable IT partner to assume 24x7x365 monitoring, Tier 1-3 support, cybersecurity governance, and rapid on-site response with contractually backed SLAs from day one. Cendien deployed a dual-response model combining remote triage with a CJIS-vetted local field engineer staged within 35 miles.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20municipal%20city%20hall%20building%20with%20police%20and%20fire%20department%20vehicles%20in%20front%20professional%20public%20safety%20infrastructure%20clean%20urban%20environment%20with%20blue%20sky%20and%20warm%20sunlight%20corporate%20case%20study%20photography&width=800&height=500&seq=res-27&orientation=landscape',
    date: 'July 2026',
    readTime: '10 min read',
    featured: false,
    downloadable: true,
    tags: ['Managed Services', 'Public Safety', 'Municipal', 'CJIS', 'Cybersecurity', 'NIST'],
    metrics: [
      { value: '30 min', label: 'Public-safety response' },
      { value: '4 hrs', label: 'Resolution target' },
      { value: '7', label: 'City sites covered' },
      { value: '$225K', label: '3-year fixed budget' },
    ],
  },
  {
    id: 28,
    slug: 'workday-augmentation-university-erp-case-study',
    title: 'Workday Augmentation: Staffing a Public University Through ERP Transformation',
    type: 'Case Study',
    topic: 'IT Staffing',
    description: 'How Cendien supplied specialized Workday functional, technical, and finance-backfill professionals to a public university undergoing a major Workday platform integration — enabling the institution to maintain Finance and Accounting continuity while internal subject-matter experts focused on configuration, testing, and go-live.',
    excerpt: 'A public university modernizing its administrative core around Workday faced a classic ERP dilemma: its best Finance and Accounting staff were needed for the transformation project, but day-to-day operations could not pause. Cendien delivered a dual-track staffing program presenting qualified candidates in as few as two business days, with a 95% fill rate and 90% retention through assignment.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20university%20campus%20with%20historic%20and%20contemporary%20buildings%20students%20walking%20on%20green%20lawns%20professional%20higher%20education%20institution%20under%20bright%20daylight%20warm%20tones%20clean%20corporate%20case%20study%20photography&width=800&height=500&seq=res-28&orientation=landscape',
    date: 'June 2026',
    readTime: '12 min read',
    featured: false,
    downloadable: true,
    tags: ['Workday', 'IT Staffing', 'Higher Education', 'ERP', 'Finance', 'Public Sector'],
    metrics: [
      { value: '2-8 days', label: 'Time to present candidates' },
      { value: '95%', label: 'Requisition fill rate' },
      { value: '90%', label: 'Retention through assignment' },
      { value: '500+', label: 'Pre-qualified network' },
    ],
  },
  {
    id: 29,
    slug: 'regional-bank-zero-trust-security-case-study',
    title: 'Regional Bank Implements Zero Trust Across 400 Branches — 60% Fewer Security Incidents',
    type: 'Case Study',
    topic: 'Cybersecurity',
    description: 'How Cendien helped an $18B regional bank replace perimeter-based security with a full Zero Trust Architecture across 400 branch locations and 5,000 employees — achieving 60% fewer security incidents and passing every regulatory audit since deployment.',
    excerpt: 'When a regional bank\'s legacy perimeter security model proved unable to keep pace with modern threats — from credential-based attacks to insider risks — the institution needed a security transformation that balanced rigorous protection with operational continuity across hundreds of locations. Cendien designed and rolled out a Zero Trust Architecture grounded in identity-first access, continuous verification, and micro-segmentation.',
    image: 'https://readdy.ai/api/search-image?query=Regional%20bank%20corporate%20headquarters%20with%20digital%20security%20shield%20and%20zero%20trust%20network%20visualization%20overlay%20in%20teal%20and%20slate%20tones%20professional%20financial%20services%20cybersecurity%20case%20study%20clean%20modern%20aesthetic&width=800&height=500&seq=res-29&orientation=landscape',
    date: 'August 2026',
    readTime: '12 min read',
    featured: false,
    downloadable: true,
    tags: ['Cybersecurity', 'Zero Trust', 'Financial Services', 'Identity', 'Compliance', 'MFA'],
    metrics: [
      { value: '400+', label: 'Branches secured' },
      { value: '60%', label: 'Fewer incidents' },
      { value: '5K', label: 'Employees protected' },
      { value: '$3.4M', label: 'Risk reduction' },
    ],
  },
  {
    id: 30,
    slug: 'municipal-workday-support-case-study',
    title: 'Municipal Government Stabilizes Workday Financials & HCM Support',
    type: 'Case Study',
    topic: 'Government & Compliance',
    description: 'How Cendien provided post-go-live Workday support for a fast-growing Texas city — covering Financials, HCM and Payroll for 3,200 employees, with reporting, integrations and release management delivered under public-sector transparency and compliance requirements.',
    excerpt: 'As a Texas city grew rapidly, its Workday Financials and HCM environment needed steady, specialized support. Cendien took on post-go-live Workday support — reporting, integrations, payroll and release management — keeping finance, HR and payroll running smoothly with clean audit trails and public-records alignment.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20municipal%20city%20hall%20building%20with%20ERP%20transformation%20technology%20dashboard%20overlay%20in%20teal%20and%20slate%20tones%20government%20digital%20transformation%20case%20study%20clean%20professional%20corporate%20photography&width=800&height=500&seq=res-30&orientation=landscape',
    date: 'August 2026',
    readTime: '14 min read',
    featured: false,
    downloadable: true,
    tags: ['Workday', 'Government', 'ERP', 'Municipal', 'Financials', 'HCM', 'Public Sector'],
    metrics: [
      { value: '3,200', label: 'Employees onboarded' },
      { value: '45%', label: 'Faster month-end close' },
      { value: '1', label: 'Unified platform' },
      { value: '$1.2M', label: 'Annual savings' },
    ],
  },
  {
    id: 31,
    slug: 'technical-consultants-staffing-state-modernization-case-study',
    title: 'Technical Consultants Staffing: Scaling a State IT Modernization Program',
    type: 'Case Study',
    topic: 'IT Staffing',
    description: 'How Cendien supplied 40+ specialized technical consultants — architects, engineers, and analysts — to staff a multi-year state government IT modernization program, with 97% retention and full background clearance compliance.',
    excerpt: 'A state government launched an ambitious multi-year IT modernization program but faced a critical talent gap: it needed dozens of specialized technical consultants with public-sector clearance and deep domain expertise, sourced quickly and compliantly. Cendien delivered a vetted bench that kept the program on schedule without straining internal resources.',
    image: 'https://readdy.ai/api/search-image?query=Professional%20IT%20consultants%20team%20collaborating%20in%20a%20modern%20government%20office%20with%20technical%20staffing%20and%20workforce%20solutions%20visualization%20in%20teal%20and%20slate%20color%20scheme%20clean%20corporate%20case%20study%20photography&width=800&height=500&seq=res-31&orientation=landscape',
    date: 'July 2026',
    readTime: '11 min read',
    featured: false,
    downloadable: true,
    tags: ['IT Staffing', 'Government', 'Technical Consultants', 'Workforce', 'Public Sector', 'Recruitment'],
    metrics: [
      { value: '40+', label: 'Consultants placed' },
      { value: '97%', label: 'Retention rate' },
      { value: '5 days', label: 'Avg. time-to-fill' },
      { value: '100%', label: 'Clearance compliance' },
    ],
  },
  {
    id: 32,
    slug: 'logistics-aws-cloud-migration-case-study',
    title: 'Logistics Leader Cuts Infrastructure Costs 35% with AWS Migration',
    type: 'Case Study',
    topic: 'Cloud & Infrastructure',
    description: 'How Cendien migrated a national logistics company\'s on-premises infrastructure to AWS — modernizing 120+ workloads, enabling elastic scaling for peak shipping seasons, and reducing infrastructure costs by 35% while improving resilience.',
    excerpt: 'A national logistics company\'s on-premises data centers could not scale to meet seasonal demand spikes, causing slowdowns during peak shipping windows. Cendien led a phased AWS migration that modernized workloads, introduced elastic auto-scaling, and applied a FinOps discipline that cut costs without sacrificing performance.',
    image: 'https://readdy.ai/api/search-image?query=Logistics%20distribution%20warehouse%20with%20cloud%20computing%20AWS%20infrastructure%20technology%20overlay%20and%20data%20visualization%20in%20teal%20and%20slate%20tones%20professional%20supply%20chain%20digital%20transformation%20case%20study&width=800&height=500&seq=res-32&orientation=landscape',
    date: 'June 2026',
    readTime: '12 min read',
    featured: false,
    downloadable: true,
    tags: ['AWS', 'Cloud Migration', 'Logistics', 'Infrastructure', 'FinOps', 'Scalability'],
    metrics: [
      { value: '35%', label: 'Cost reduction' },
      { value: '120+', label: 'Workloads modernized' },
      { value: '99.99%', label: 'Uptime' },
      { value: '3x', label: 'Faster scaling' },
    ],
  },
  {
    id: 33,
    slug: 'microsoft-dynamics-365-retail-erp-case-study',
    title: 'Retail Enterprise Unifies Finance & Operations with Microsoft Dynamics 365',
    type: 'Case Study',
    topic: 'ERP & Enterprise',
    description: 'How Cendien implemented Microsoft Dynamics 365 Finance & Operations for a multi-brand retail enterprise — consolidating six legacy ERPs into one platform, automating 70% of manual reconciliations, and cutting inventory costs by 18%.',
    excerpt: 'A multi-brand retail enterprise running six separate legacy ERP systems faced fragmented financial reporting, labor-intensive manual reconciliation, and mounting inventory carrying costs. Cendien delivered a Microsoft Dynamics 365 Finance & Operations implementation that unified the entire business and automated previously manual finance processes.',
    image: 'https://readdy.ai/api/search-image?query=Retail%20enterprise%20headquarters%20with%20Microsoft%20Dynamics%20365%20ERP%20dashboard%20on%20large%20monitors%20showing%20financial%20and%20operations%20analytics%20in%20teal%20and%20slate%20tones%20professional%20corporate%20technology%20case%20study&width=800&height=500&seq=res-33&orientation=landscape',
    date: 'June 2026',
    readTime: '13 min read',
    featured: false,
    downloadable: true,
    tags: ['Microsoft Dynamics 365', 'ERP', 'Retail', 'Finance', 'Implementation', 'Automation'],
    metrics: [
      { value: '6', label: 'ERPs unified' },
      { value: '70%', label: 'Manual reconciliation automated' },
      { value: '18%', label: 'Inventory cost reduction' },
      { value: '$2.4M', label: 'Annual savings' },
    ],
  },
];

export const resourceTypes = ['All', 'Whitepaper', 'Case Study', 'Guide', 'Checklist', 'Webinar', 'Infographic'];
export const resourceTopics = [
  'All Topics',
  'ERP & Enterprise',
  'Healthcare IT',
  'Cybersecurity',
  'Cloud & Infrastructure',
  'Government & Compliance',
  'IT Staffing',
  'Analytics & AI',
  'Managed Services',
  'Strategy',
];
